// Implements: F-UIX-009
/**
 * The active organization, DERIVED from the server's scopes (design DD9b).
 *
 * The available organizations are always
 * `scopes.filter((s) => s.kind === "organization")` of the `ScopesView` that
 * just arrived, never a copy kept somewhere. The recalculation happens every
 * time `["session","scopes"]` delivers data — boot, retry, invalidation after
 * signing in — so a membership revoked on the server disappears from the
 * interface on the next boot without anybody deleting it by hand.
 *
 * What is persisted is ONE identifier, under `prototyper.activeOrganization`,
 * and it is a PREFERENCE, never an authorisation. The remembered identifier is
 * honoured only while it is still in the fresh list; when it is not, it is
 * dropped in silence and the first organization by name is chosen instead.
 * With an empty list the state is "no organization": a read-only surface, not
 * an error.
 *
 * What is deliberately NOT stored is the organization itself. Painting a name
 * and a role from storage before `ScopesView` arrives is faster and is exactly
 * the failure the capability forbids: the interface would show a role the
 * server no longer grants, and the first `403` would be what reveals it.
 *
 * This file is one of the modules `eslint.config.js` allows to name
 * `localStorage`; the others are `shared/theme/` and `shared/ui/panelMemory.ts`
 * (the `prototyper.panel.<id>` family of F-UIX-005, a state word per panel and
 * nothing about a person). Between them they own every key the application
 * writes (DD22), and `webStorage.test.ts` audits all of them.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import type { ScopeEntry } from "../../types/api";
import { PLATFORM_LOCALE } from "../../types/locale";
import { useSession } from "./session";

/** The one key this module owns. Only an identifier is ever written to it. */
export const ACTIVE_ORGANIZATION_STORAGE_KEY = "prototyper.activeOrganization";

/** An organization scope, with the `id` and `name` the wire may leave null resolved. */
export interface OrganizationScope {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

export interface ActiveOrganizationValue {
  /** Every organization the fresh `ScopesView` grants, ordered by name. */
  readonly organizations: readonly OrganizationScope[];
  /** The active one, or `null` for the "no organization" state. */
  readonly active: OrganizationScope | null;
  /** Remember a different organization, by identifier. */
  readonly select: (id: string) => void;
}

const ActiveOrganizationContext = createContext<ActiveOrganizationValue | null>(null);

/** The organizations of a scope list, ordered by name (the platform locale's collation). */
export function organizationsOf(scopes: readonly ScopeEntry[]): OrganizationScope[] {
  return scopes
    .filter(
      (scope): scope is ScopeEntry & { id: string; name: string } =>
        scope.kind === "organization" && scope.id !== null && scope.name !== null,
    )
    .map((scope) => ({ id: scope.id, name: scope.name, role: scope.role }))
    .sort((left, right) => left.name.localeCompare(right.name, PLATFORM_LOCALE));
}

/**
 * The organization a remembered identifier selects.
 *
 * @param organizations The fresh list, already ordered by name.
 * @param remembered The identifier read from storage, or chosen by hand.
 * @returns The remembered one while it is still granted, the first by name
 *   otherwise, and `null` when the list is empty.
 */
export function chooseActive(
  organizations: readonly OrganizationScope[],
  remembered: string | null,
): OrganizationScope | null {
  const kept = organizations.find((organization) => organization.id === remembered);
  return kept ?? organizations[0] ?? null;
}

/**
 * The remembered identifier, or nothing.
 *
 * Guarded because the PROPERTY ACCESS itself throws `SecurityError` where site
 * data is blocked — a partitioned third-party context, an enterprise policy, a
 * browser told to refuse storage — and this function is the state initialiser
 * of a provider mounted above `RouterProvider` with no boundary over it. An
 * exception here is a blank document, not a forgotten preference.
 */
function readRemembered(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remember the choice where storage allows it. A refusal is not an error. */
function writeRemembered(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
    else localStorage.setItem(ACTIVE_ORGANIZATION_STORAGE_KEY, id);
  } catch {
    // Storage denied: the preference is lost for this browser, and every boot
    // simply derives the first organization by name instead. The session, the
    // scopes and the interface are untouched.
  }
}

export interface ActiveOrganizationProviderProps {
  readonly children: ReactNode;
}

/**
 * Mounted INSIDE `SessionProvider`, because it does not exist without it.
 *
 * The derivation is a `useMemo` over the session state rather than an effect
 * that writes state: everything it needs is already known while rendering, and
 * an effect would render once with the wrong answer before correcting itself.
 * The only effect here writes the identifier to storage, and it runs solely
 * while the session is authenticated — clearing the preference during the
 * loading tick of every reload would delete it on the way in.
 */
export function ActiveOrganizationProvider({
  children,
}: ActiveOrganizationProviderProps): ReactElement {
  const { state } = useSession();
  const [remembered, setRemembered] = useState<string | null>(readRemembered);

  const organizations = useMemo<readonly OrganizationScope[]>(
    () => (state.status === "authenticated" ? organizationsOf(state.view.scopes) : []),
    [state],
  );
  const active = useMemo(() => chooseActive(organizations, remembered), [organizations, remembered]);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    // Writing the derived choice — not only an explicit one — is what keeps an
    // orphaned identifier from surviving a revoked membership: the value in
    // storage is always one the server granted in the answer we just read.
    writeRemembered(active?.id ?? null);
  }, [state.status, active]);

  const select = useCallback((id: string): void => {
    setRemembered(id);
  }, []);

  const value = useMemo<ActiveOrganizationValue>(
    () => ({ organizations, active, select }),
    [organizations, active, select],
  );

  return <ActiveOrganizationContext value={value}>{children}</ActiveOrganizationContext>;
}

/** The derived organizations and the active one, from the nearest provider. */
export function useActiveOrganization(): ActiveOrganizationValue {
  const value = useContext(ActiveOrganizationContext);
  if (value === null) {
    throw new Error("useActiveOrganization must be called inside an ActiveOrganizationProvider");
  }
  return value;
}
